import qs from 'qs';
import { axiosClient } from '@/libs/axios-client';

const API_BASE = '/partner-requests';

// ============================================
// Requests to a company's partners for their analytic cards
// ============================================

const paramsSerializer = (params) => qs.stringify(params, { arrayFormat: 'brackets' });

const fileNameFrom = (disposition, fallback) => {
  const match = /filename\*=UTF-8''([^;]+)/i.exec(disposition || '');
  return match ? decodeURIComponent(match[1]) : fallback;
};

const data = (response) => response.data.data;

/** Accounts of the year with partners on them. */
export const getAccounts = async (companyId, asOf) =>
  data(await axiosClient.get(`${API_BASE}/${companyId}/accounts`, { params: { asOf } }));

/** A company's requests, newest first. */
export const getRequests = async (companyId) => data(await axiosClient.get(`${API_BASE}/${companyId}`));

/** New request; the recipients come from the books. */
export const createRequest = async (companyId, input) =>
  data(await axiosClient.post(`${API_BASE}/${companyId}`, input));

/** One request with its counts. */
export const getRequest = async (companyId, requestId) =>
  data(await axiosClient.get(`${API_BASE}/${companyId}/${requestId}`));

/** The text and the sender. */
export const updateTemplate = async (companyId, requestId, input) =>
  data(await axiosClient.patch(`${API_BASE}/${companyId}/${requestId}`, input));

/** New partners and balances from the books; decisions stay. */
export const refreshRecipients = async (companyId, requestId) =>
  data(await axiosClient.post(`${API_BASE}/${companyId}/${requestId}/refresh`));

/** One page of recipients: { count, page, limit, items }. */
export const getRecipients = async (companyId, requestId, params) =>
  data(await axiosClient.get(`${API_BASE}/${companyId}/${requestId}/recipients`, { params, paramsSerializer }));

/** A person's decision on one recipient. */
export const updateRecipient = async (companyId, requestId, recipientId, input) =>
  data(await axiosClient.patch(`${API_BASE}/${companyId}/${requestId}/recipients/${recipientId}`, input));

/** Confirms proposed addresses: of the picked `ids`, else of everything `filter` shows. */
export const confirmRecipients = async (companyId, requestId, input) =>
  data(await axiosClient.post(`${API_BASE}/${companyId}/${requestId}/recipients/confirm`, input));

/** Marks replies on the picked recipients; '' takes the mark back. */
export const markReply = async (companyId, requestId, input) =>
  data(await axiosClient.post(`${API_BASE}/${companyId}/${requestId}/recipients/reply`, input));

/** The filtered list as Excel, which is also the import's template. */
export const exportRecipients = async (companyId, requestId, filters, labels) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/${requestId}/recipients/export`, {
    params: { ...filters, labels: JSON.stringify(labels) },
    paramsSerializer,
    responseType: 'blob',
  });
  return {
    blob: response.data,
    fileName: fileNameFrom(response.headers['content-disposition'], 'partners.xlsx'),
  };
};

/** Reads an edited Excel and checks every row; nothing is saved. */
export const previewImport = async (companyId, requestId, file) => {
  const form = new FormData();
  form.append('file', file);
  return data(await axiosClient.post(`${API_BASE}/${companyId}/${requestId}/recipients/import/preview`, form));
};

/** Saves the rows looked at in the preview. */
export const applyImport = async (companyId, requestId, rows) =>
  data(await axiosClient.post(`${API_BASE}/${companyId}/${requestId}/recipients/import`, { rows }));

/** The mail as a recipient would get it today. */
export const previewMail = async (companyId, requestId, recipient) =>
  data(await axiosClient.get(`${API_BASE}/${companyId}/${requestId}/preview`, { params: { recipient } }));

/** The real text to an internal address; unlocks sending for this version. */
export const sendTest = async (companyId, requestId, input) =>
  data(await axiosClient.post(`${API_BASE}/${companyId}/${requestId}/test`, input));

/** Starts a wave: { limit, confirmCount, acknowledgeStop }. */
export const startWave = async (companyId, requestId, input) =>
  data(await axiosClient.post(`${API_BASE}/${companyId}/${requestId}/start`, input));

/** Stops the sending at once. */
export const stopSending = async (companyId, requestId) =>
  data(await axiosClient.post(`${API_BASE}/${companyId}/${requestId}/stop`));

/** The one reminder, to who has not answered after the deadline. */
export const startReminder = async (companyId, requestId, input) =>
  data(await axiosClient.post(`${API_BASE}/${companyId}/${requestId}/reminder`, input));

/** What arrived in the request's mailbox: { count, page, limit, items }. */
export const getInbox = async (companyId, requestId, params) =>
  data(await axiosClient.get(`${API_BASE}/${companyId}/${requestId}/inbox`, { params }));

/** Reads the mailbox now. */
export const checkMailbox = async (companyId, requestId) =>
  data(await axiosClient.post(`${API_BASE}/${companyId}/${requestId}/inbox/check`));

/** A person says which partner a message belongs to. */
export const assignMessage = async (companyId, requestId, messageId, recipient) =>
  data(await axiosClient.post(`${API_BASE}/${companyId}/${requestId}/inbox/${messageId}/assign`, { recipient }));
